import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { EM_DASH } from 'libs.constants';
import { Button, useLocalizeMessage, Spinner, H1, Card, useToastNotification } from 'libs.nucleus';
import { useBooleanState } from 'libs.react';
import { resendCaregiverInvite } from './helpers/caregiver_form.helpers';
import { getApiErrorMessage } from '../../helpers/api.helpers';
import { getCaregiverDisplayNameCount } from '../../helpers/caregivers.helpers';
import { getParticipantNumber } from '../../helpers/participants.helpers';
import { useCopyToClipboard } from '../../hooks/use_copy_to_clipboard';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useLumosAuth } from '../../hooks/use_lumos_auth';
import { useOrgContext } from '../../hooks/use_org_context';
import { useSiteContext } from '../../hooks/use_site_context';
import { EParticipantInviteStatus } from '../../types/participants.type';
import { LUMOS_PERMISSIONS } from '../../types/permissions.types';
import { CopyIcon } from '../svgIcons/copy.icon';
export const CaregiverInvite = ({ caregiver, onDone, onBusy, studyCode, participant }) => {
    const translate = useLocalizeMessage();
    const org = useOrgContext();
    const { environment } = useCurrentEnvironment();
    const site = useSiteContext();
    const { addNotification } = useToastNotification();
    const [, copy] = useCopyToClipboard();
    const queryClient = useQueryClient();
    const { isUserPermitted } = useLumosAuth();
    const isResendForExpiredInvitation = caregiver.c_public_user.c_invite === EParticipantInviteStatus.EXPIRED;
    const [isLoading, setIsLoadingTrue, setIsLoadingFalse] = useBooleanState(isResendForExpiredInvitation);
    const sendCaregiverInviteMutation = useMutation({
        mutationFn: resendCaregiverInvite,
        onMutate: () => {
            onBusy();
        },
        onSuccess: () => {
            if (!isResendForExpiredInvitation) {
                addNotification({
                    title: '',
                    subtitle: translate('status.participant_invite.sent'),
                    type: 'success',
                });
            }
            else {
                queryClient.invalidateQueries({
                    queryKey: ['participantDetails', participant._id],
                });
            }
        },
        onError: (e) => {
            const message = getApiErrorMessage(e);
            addNotification({
                title: '',
                subtitle: message ?? translate('error.generic'),
                type: 'error',
            });
        },
        onSettled: () => {
            if (isResendForExpiredInvitation) {
                setIsLoadingFalse();
            }
            onDone();
        },
    });
    const handleCopy = (text, label) => () => {
        copy(text)
            .then(() => {
            addNotification({
                title: '',
                subtitle: label,
                type: 'success',
            });
        })
            .catch(() => {
            addNotification({
                title: '',
                subtitle: translate('error.caregiver.invite.copy_failed'),
                type: 'error',
            });
        });
    };
    const resendInvite = async () => {
        if (!environment) {
            throw new Error('Environment is not defined');
        }
        if (!studyCode) {
            return;
        }
        if (isResendForExpiredInvitation) {
            setIsLoadingTrue();
        }
        sendCaregiverInviteMutation.mutate({
            org,
            site,
            environment,
            locale: participant.c_locale,
            studyCode,
            caregiver,
            sendInviteEmail: !isResendForExpiredInvitation,
        });
    };
    const displayName = translate('label.caregiver.count', {
        count: getCaregiverDisplayNameCount(caregiver.c_caregiver_display_name),
    });
    useEffect(() => {
        if (isResendForExpiredInvitation) {
            resendInvite();
        }
    }, []);
    if (isLoading) {
        return (_jsx("div", { className: 'flex flex-col h-full items-center justify-center', children: _jsx(Spinner, { size: 'xl' }) }));
    }
    return (_jsxs(Card.Content, { className: 'p-4 border border-gray-200 rounded-lg shadow-sm sm:p-8 relative flex flex-col justify-center bg-neutral-50 p-4 gap-y-4 w-1/3', children: [_jsxs("section", { children: [_jsx("h4", { className: 'nucleus-text-h4 text-text-secondary', children: displayName }), _jsxs("p", { className: 'nucleus-text-sm text-text-secondary', children: [translate('generic.id'), ": ", _jsx("span", { children: getParticipantNumber(caregiver.c_public_user) })] })] }), _jsxs("section", { className: 'flex flex-col gap-4 bg-neutral-100 p-4 rounded-lg', children: [_jsxs("section", { children: [_jsx("h5", { className: 'text-text-secondary', children: translate('label.study_code') }), _jsxs("div", { className: 'flex flex-row', children: [_jsx(H1, { children: studyCode ?? EM_DASH }), _jsx(Button, { size: 'sm', variant: 'transparent', onClick: handleCopy(studyCode, translate('info.study_code.copied')), label: translate('action.caregiver.study_code_copy'), content: _jsx(CopyIcon, { className: 'text-text-secondary' }), dataTestId: 'copy-study-code-button' })] })] }), _jsxs("section", { children: [_jsx("h5", { className: 'text-text-secondary', children: translate('label.invite_code') }), _jsxs("div", { className: 'flex flex-row', children: [_jsx(H1, { children: caregiver.c_public_user.c_access_code ?? EM_DASH }), caregiver.c_public_user.c_access_code && (_jsx(Button, { size: 'sm', variant: 'transparent', onClick: handleCopy(caregiver.c_public_user.c_access_code, translate('info.invite_code.copied')), label: translate('action.caregiver.invite_code_copy'), content: _jsx(CopyIcon, { className: 'text-text-secondary' }), dataTestId: 'copy-invite-code-button' }))] })] })] }), _jsx("section", { className: 'w-full flex flex-row justify-end', children: isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_EMAIL_CAREGIVER_INVITE) && (_jsx(Button, { disabled: !caregiver.c_email || sendCaregiverInviteMutation.isPending, variant: 'secondary', onClick: resendInvite, label: translate('label.caregiver.email_codes'), content: sendCaregiverInviteMutation.isPending ? (_jsxs("div", { className: 'flex items-center gap-3', children: [_jsx(Spinner, { size: 'sm' }), " ", translate('status.participant_invite.sending_email'), "..."] })) : undefined, dataTestId: 'email-codes-to-caregiver-button' })) })] }));
};
